import React from "react";
import LegendComponent from "@components/LegendComponent";
import { resolveStatusColumn } from "@helpers/helpers";
import clsx from "clsx";
import {
  COLUMN_SIZES,
  LEGEND_DATA,
  LEGEND_HIDE_TAGS,
  SCHEDULED_CHECK_MARK,
  SCHEDULED_X_MARK,
  SCHEDULED_CELL_PADDING,
  STATUS_CHECK_VALUES,
} from "../constants";

// Stream Status Column
const buildStreamStatusColumn = (translate) => {
  return {
    accessorKey: "ReconciliationStreamStatusName",
    header: translate(
      "reconciliation-streams.columns.ReconciliationStreamStatus"
    ),
    headerHelper: () => {
      return (
        <LegendComponent
          data={LEGEND_DATA}
          dataToHide={LEGEND_HIDE_TAGS}
        />
      );
    },
    size: COLUMN_SIZES.STATUS,
    sortable: true,
    alignment: "left",
    cell: ({ row }) => {
      return resolveStatusColumn(
        row.original.ReconciliationStreamStatusName,
        row.original.ReconciliationStreamStatusTag,
        STATUS_CHECK_VALUES,
        null,
        row.original.ReconciliationRunStatusTag,
        translate,
      );
    },
  };
};

// Stream Name Column
const buildStreamNameColumn = (translate) => {
  return {
    accessorKey: "ReconciliationStreamName",
    header: translate(
      "reconciliation-streams.columns.ReconciliationStreamName"
    ),
    hideCopy: true,
    hideFilter: true,
    isMultiple: false,
    sortable: true,
    size: COLUMN_SIZES.STREAM_NAME,
    alignment: "left",
    processFilterValue: (value, row) => {
      return row.ReconciliationStreamTag;
    },
    processFilterKey: (value, row) => {
      return "ReconciliationStreamTag";
    },
  };
};

// Scheduled Column
const buildScheduledColumn = (translate) => {
  return {
    accessorKey: "IsScheduled",
    header: translate("reconciliation-streams.columns.Scheduled"),
    size: COLUMN_SIZES.SCHEDULED,
    isMultiple: false,
    sortable: true,
    truncate: false,
    filterCheck: (value) => value === true,
    hideCopy: true,
    cell: ({ row }) => {
      return (
        <span
          className={clsx(
            `w-full flex justify-center`,
            !row.original.IsScheduled ? "text-red-400 " : "text-green-400"
          )}
        >
          <span
            className={`rounded-full flex items-end justify-center gap-1 w-fit font-normal text-sm ${SCHEDULED_CELL_PADDING} py- border border-r-10 border-transparent `}
          >
            <span className="text-[15px]">
              {!row.original.IsScheduled ? SCHEDULED_X_MARK : SCHEDULED_CHECK_MARK}
            </span>
          </span>
        </span>
      );
    },
  };
};

// Periodicity Name Column
const buildPeriodicityNameColumn = (translate) => {
  return {
    accessorKey: "PeriodicityName",
    header: translate("reconciliation-streams.columns.PeriodicityName"),
    size: COLUMN_SIZES.PERIODICITY_NAME,
    sortable: true,
    hideCopy: true,
    hideFilter: true,
    alignment: "left",
  };
};

// Next Reconciliation Run Column
const buildNextReconciliationRunColumn = (translate) => {
  return {
    accessorKey: "NextReconciliationRun",
    header: translate("reconciliation-streams.columns.NextReconciliationRun"),
    size: COLUMN_SIZES.NEXT_RUN,
    canFilter: false,
    splitCellValue: "T",
  };
};

// Last Successful Reconciliation Run Column
const buildLastSuccessfulRunColumn = (translate) => {
  return {
    accessorKey: "LastSuccessfulReconciliationRun",
    header: translate(
      "reconciliation-streams.columns.LastSuccessfulReconciliationRun"
    ),
    size: COLUMN_SIZES.LAST_SUCCESSFUL_RUN,
    canFilter: false,
    splitCellValue: "T",
  };
};

// Last Duration Column
const buildLastDurationColumn = (translate) => {
  return {
    accessorKey: "LastDuration",
    header: translate("reconciliation-streams.columns.LastDuration"),
    size: COLUMN_SIZES.LAST_DURATION,
    canFilter: false,
  };
};

// Build all columns
export const buildColumns = (translate) => {
  return [
    buildStreamStatusColumn(translate),
    buildStreamNameColumn(translate),
    buildScheduledColumn(translate),
    buildPeriodicityNameColumn(translate),
    buildNextReconciliationRunColumn(translate),
    buildLastSuccessfulRunColumn(translate),
    buildLastDurationColumn(translate),
  ];
};
